import { useState } from "react";
import { VOCAB, MISSIONS } from "../data/vocab";
import { recallProbability, getStatus } from "../hooks/useHLR";

export default function WordList({ wordStates, loading, onPractice }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#b0a898" }}>Loading…</div>;

  const now = Date.now() / 3600000;

  const seenWords = VOCAB
    .map(v => {
      const ws = wordStates[v.id];
      if (!ws || !ws.lastReviewTime || ws.lastReviewTime === 0) return null;
      const p = recallProbability(ws, now);
      const status = getStatus(ws);
      const mission = MISSIONS.find(m => m.wordIds.includes(v.id));
      return { ...v, ws, p, status, mission };
    })
    .filter(Boolean);

  const filtered = seenWords.filter(item => {
    const mf = filter === "all" ? true :
      filter === "strong" ? item.status === "Strong" :
      filter === "review" ? (item.status === "At Risk" || item.status === "Weak") :
      true;
    const ms = !search || item.word.includes(search) || item.translation.toLowerCase().includes(search.toLowerCase());
    return mf && ms;
  });

  // Determine which IDs to practice based on current filter
  const practiceIds = filtered.map(w => w.id);
  const practiceLabel = filter === "strong" ? "Strong words" : filter === "review" ? "Words to review" : "All seen words";

  const handleWordClick = (clickedItem) => {
    if (!onPractice || practiceIds.length === 0) return;
    const ids = [clickedItem.id, ...practiceIds.filter(id => id !== clickedItem.id)];
    onPractice(`Practice: ${clickedItem.word}`, ids, clickedItem.id);
  };

  const handlePracticeAll = () => {
    if (!onPractice || practiceIds.length === 0) return;
    onPractice(practiceLabel, practiceIds);
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: "#111", marginBottom: 4, letterSpacing: -0.4 }}>Lexicon</h2>
          <p style={{ fontSize: 13, color: "#b0a898" }}>Your collection of {seenWords.length} learned word{seenWords.length !== 1 ? "s" : ""}</p>
        </div>
        {practiceIds.length > 0 && onPractice && (
          <button onClick={handlePracticeAll} style={{ display: "flex", alignItems: "center", gap: 7, background: "#1a8f68", color: "#fff", border: "none", borderRadius: 11, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
            Practice {filter === "all" ? "all" : filter === "strong" ? "strong" : "review"} words
          </button>
        )}
      </div>

      {seenWords.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 18, border: "1.5px solid #ede8df", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: "#f5f0e8", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#b0a898" strokeWidth="1.8" strokeLinecap="round"><path d="M4 5h16a2 2 0 012 2v18a2 2 0 01-2 2H4V5z"/><path d="M22 11h4a2 2 0 012 2v10a2 2 0 01-2 2h-4"/></svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 6 }}>Your lexicon is empty</div>
          <div style={{ fontSize: 13, color: "#b0a898" }}>Start a mission to begin learning Persian words.</div>
        </div>
      ) : (
        <>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <div style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, color: "#c0b8ae" }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10.5 10.5l3 3"/></svg>
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your words…" style={{ width: "100%", padding: "11px 12px 11px 38px", border: "1.5px solid #e8e2d8", borderRadius: 12, fontSize: 13, color: "#111", background: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {[
              { id: "all", label: `All (${seenWords.length})` },
              { id: "strong", label: `Strong (${seenWords.filter(w => w.status === "Strong").length})` },
              { id: "review", label: `Need review (${seenWords.filter(w => w.status === "At Risk" || w.status === "Weak").length})` },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)} style={{
                padding: "7px 15px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: "1.5px solid", borderColor: filter === f.id ? "#1a8f68" : "#e8e2d8",
                background: filter === f.id ? "#1a8f68" : "#fff",
                color: filter === f.id ? "#fff" : "#888",
                cursor: "pointer", fontFamily: "inherit",
              }}>{f.label}</button>
            ))}
          </div>

          {onPractice && filtered.length > 0 && (
            <div style={{ fontSize: 11, color: "#b0a898", marginBottom: 10, fontWeight: 600 }}>
              Tap any word to practice it as a flashcard
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((item, i) => {
              const dotColor = item.status === "Strong" ? "#1a8f68" : item.status === "At Risk" ? "#f0a020" : "#e24b4a";
              const statusLabel = item.status === "Strong" ? "Strong" : item.status === "At Risk" ? "Review soon" : "Needs practice";
              return (
                <button
                  key={item.id}
                  onClick={() => handleWordClick(item)}
                  style={{
                    background: "#fff", borderRadius: 13, padding: "14px 16px",
                    border: "1px solid #ede8df", display: "flex", alignItems: "center", gap: 14,
                    cursor: onPractice ? "pointer" : "default",
                    textAlign: "left", fontFamily: "inherit", width: "100%",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (onPractice) { e.currentTarget.style.background = "#f5f0e8"; e.currentTarget.style.borderColor = "#c8c0b4"; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#ede8df"; }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f5f0e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#b0a898", flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ minWidth: 80 }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#111", direction: "rtl", fontFamily: "Scheherazade New, Vazirmatn, Georgia, serif" }}>{item.word}</div>
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: "#555" }}>{item.translation}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#888" }}>{statusLabel}</span>
                  </div>
                  {onPractice && (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#c0b8ae" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M3 8h10M9 4l4 4-4 4"/></svg>
                  )}
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 24px", color: "#b0a898", fontSize: 13 }}>No words match your filter.</div>
          )}
        </>
      )}
    </div>
  );
}
